import { CollapsibleItem, Heading } from '@lmc/ui-kit'
import { ListItem } from '@lmc/ui-kit/src/dataDisplay'
import type { ToggleCollapseFn } from '@lmc/ui-kit/src/dataDisplay/CollapsibleItem/CollapsibleItem'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import { capitalize, lowerFirst, toLower } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  DAY_FIRST_DATE_FORMAT,
  getDateTimeInZone,
  TIME_FORMAT
} from 'screens/Rostering/utils.datetime'
import { styled } from 'styled-components'
import type { AllocationWindowRevisionDetail } from '../AllocationWindow'
import ServiceUserInfo from './ServiceUserInfo'
import SkillsToMatchContainer from './SkillsToMatchContainer'

type AllocationDetailedInformationProps = {
  revisionDetail: AllocationWindowRevisionDetail
}

const AllocationDetailedInformation = ({
  revisionDetail
}: AllocationDetailedInformationProps) => {
  const { t } = useTranslation()

  const isOvernight =
    getDateTimeInZone(revisionDetail.startTime).date() <
    getDateTimeInZone(revisionDetail.endTime).date()

  return (
    <CollapsibleItemStyled
      header={(toggleCollapse: ToggleCollapseFn, collapsed: boolean) => (
        <ListItemStyled
          onClick={toggleCollapse}
          toggleCollapse={toggleCollapse}
          isCollapsed={collapsed}
          title={
            <SectionWrapper>
              <TitleSection>
                <Section>
                  <StyledText>{`${capitalize(
                    revisionDetail.siteShift ? t('SHIFT') : t('VISIT')
                  )} / ${revisionDetail.instanceType}`}</StyledText>
                  <Heading level={4}>{revisionDetail.title}</Heading>
                </Section>
                {!revisionDetail.siteShift && (
                  <ServiceUserInfo serviceUser={revisionDetail.resident} />
                )}
              </TitleSection>
              <StyledDivider type="vertical" />
              <InfoSection>
                <StyledText level={2}>{t('DATE')}</StyledText>
                <Heading level={5} weight="medium">
                  {getDateTimeInZone(revisionDetail.startTime).format(
                    DAY_FIRST_DATE_FORMAT
                  )}
                </Heading>
              </InfoSection>
              <InfoSection>
                <StyledText level={2}>{t('TIME_WINDOW')}</StyledText>
                <FlexiSection>
                  <HoursSection>
                    <Heading level={5} weight="medium">
                      {`${getDateTimeInZone(revisionDetail.startTime).format(
                        TIME_FORMAT
                      )} - ${getDateTimeInZone(revisionDetail.endTime).format(
                        TIME_FORMAT
                      )}`}
                    </Heading>
                    {isOvernight && (
                      <OvernightText level={2}>{t('PLUS_ONE')}</OvernightText>
                    )}
                  </HoursSection>
                  {revisionDetail.flexiType && (
                    <Text level={2}>{`· ${t('FLEXI')}`}</Text>
                  )}
                </FlexiSection>
              </InfoSection>
              <InfoSection>
                <StyledText level={2}>{t('PLANNED_DURATION')}</StyledText>
                <Heading level={5} weight="medium">
                  {revisionDetail.expectedDuration}
                </Heading>
              </InfoSection>
              <StyledDivider type="vertical" />
            </SectionWrapper>
          }
        />
      )}
    >
      <MoreInfoSection>
        <DescriptionRow>
          <DescriptionSection>
            <StyledText level={2}>{`${capitalize(
              revisionDetail.siteShift ? t('SHIFT') : t('VISIT')
            )} ${lowerFirst(t('DESCRIPTION'))}`}</StyledText>
            {revisionDetail.description ? (
              <Text>{revisionDetail.description}</Text>
            ) : (
              <StyledLightText>{t('NO_DESCRIPTION')}</StyledLightText>
            )}
          </DescriptionSection>
          {!revisionDetail.siteShift && (
            <Section>
              <StyledText level={2}>{t('FUNDING_TYPE')}</StyledText>
              <Text>{revisionDetail.fundingType}</Text>
            </Section>
          )}
        </DescriptionRow>
        <DescriptionRow>
          <TeamMembersSection>
            <StyledText level={2}>{t('TEAM_MEMBERS_REQUIRED')}</StyledText>
            <Text>{`${revisionDetail.numberSupply} ${toLower(
              t('TEAM_MEMBERS')
            )}`}</Text>
          </TeamMembersSection>
          <SkillsSection>
            <StyledText level={2}>{t('SKILLS_TO_MATCH')}</StyledText>
            <SkillsToMatchContainer skillsIds={revisionDetail.skills} />
          </SkillsSection>
        </DescriptionRow>
      </MoreInfoSection>
    </CollapsibleItemStyled>
  )
}

const CollapsibleItemStyled = styled(CollapsibleItem)`
  background-color: ${(props) => props.theme.color.neutral.color10};

  border-radius: 20px;

  :hover {
    background-color: ${(props) => props.theme.color.neutral.color10};
  }
`

const ListItemStyled = styled(ListItem)`
  padding: 24px;

  cursor: pointer;
`

const SectionWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 24px;
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 5vw;
  gap: 8px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
`

const TeamMembersSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 240px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 185px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledDivider = styled(Divider)`
  height: 88px;
  width: 2px;
  color: ${(props) => props.theme.color.neutral.color7};
`
const FlexiSection = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const MoreInfoSection = styled.div`
  padding: 0 24px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DescriptionRow = styled.div`
  display: flex;
  gap: 24px;
`

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 612px;
`
const SkillsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const StyledLightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
const OvernightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  position: relative;
  top: 0;
  transform: translateY(-10%);
`
const HoursSection = styled.div`
  display: flex;
  gap: 4px;
`

export default AllocationDetailedInformation
